import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, ShieldCheck, Car, BadgeCheck, HandHeart, Sparkles, Phone, FileText, Upload, CheckCircle, Users, Clock, Award, Wallet, HeartHandshake, Search, PhoneCall, UserCheck, GraduationCap, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import axios from 'axios';
import { SEOHead, faqSchema } from '../components/SEOHead';
import { COMPANY_INFO } from '../lib/utils';
import { SAP_NUMBER } from '../lib/cities-data';
import { VIEWPORT_ONCE } from '../lib/motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] } }
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const POSTES = [
  { value: 'menage', label: 'Easer Ménage / Repassage' },
  { value: 'jardinage', label: 'Easer Jardinage' },
  { value: 'bricolage', label: 'Easer Bricolage' },
  { value: 'polyvalent', label: 'Easer Polyvalent' },
];

const EXPERIENCES = ['Débutant(e) motivé(e)', 'Moins de 1 an', '1 à 3 ans', '3 à 5 ans', 'Plus de 5 ans'];

const AVANTAGES = [
  { icon: BadgeCheck, title: 'Poste en CDI', desc: 'Un contrat stable dès l\'embauche, parce que la confiance se construit dans la durée.' },
  { icon: HeartHandshake, title: 'Mutuelle d\'entreprise', desc: 'Une couverture santé pour vous et votre famille, prise en charge par Ptitservices.' },
  { icon: Wallet, title: 'Primes', desc: 'Votre engagement et la satisfaction de vos clients sont reconnus et récompensés.' },
  { icon: Users, title: 'Clients attitrés', desc: 'Vous suivez les mêmes foyers : vous connaissez les lieux, les habitudes, les personnes.' },
  { icon: Clock, title: 'Planning organisé', desc: 'Des tournées cohérentes près de chez vous, construites avec vous par l\'agence.' },
  { icon: Award, title: 'Formation & suivi', desc: 'Intégration accompagnée, bonnes pratiques et un référent agence toujours joignable.' },
];

const PREREQUIS = [
  { icon: Car, title: 'Permis B et véhicule personnel', desc: 'Obligatoires : nos Easers se déplacent chez les clients dans un rayon de 40 km autour de Servins.', required: true },
  { icon: Heart, title: 'Bienveillance et amour du métier', desc: 'Vous aimez rendre service, prendre soin d\'un lieu et des personnes qui y vivent. C\'est le cœur du métier d\'Easer.', required: true },
  { icon: ShieldCheck, title: 'Fiabilité et discrétion', desc: 'Ponctualité, respect de l\'intimité des foyers et sens des responsabilités.', required: true },
  { icon: Sparkles, title: 'Expérience appréciée', desc: 'Ménage, repassage, jardinage ou bricolage : une première expérience est un plus, la motivation compte autant.', required: false },
];

const ETAPES = [
  { icon: FileText, num: '01', title: 'Votre candidature', desc: 'Vous déposez votre CV et quelques informations via le formulaire ci-dessous. Accusé de réception immédiat.' },
  { icon: PhoneCall, num: '02', title: 'Premier échange téléphonique', desc: 'Sous 5 jours ouvrés, nous vous appelons pour parler de votre parcours, de vos envies et de vos disponibilités.' },
  { icon: UserCheck, num: '03', title: 'Entretien en agence', desc: 'Rencontre à Servins : nous vérifions ensemble vos compétences, votre savoir-être et vos références.' },
  { icon: Search, num: '04', title: 'Mise en situation', desc: 'Une intervention d\'essai accompagnée par un Easer expérimenté pour valider les gestes et la qualité attendue.' },
  { icon: GraduationCap, num: '05', title: 'Intégration & suivi', desc: 'Signature du CDI, formation à nos méthodes, présentation à vos premiers clients et suivi régulier par l\'agence.' },
];

const FAQ = [
  { q: 'Faut-il avoir de l\'expérience pour devenir Easer ?', a: 'Une première expérience en ménage, repassage, jardinage ou bricolage est appréciée mais pas indispensable. Nous formons nos Easers à nos méthodes. Ce qui compte avant tout : la bienveillance, la fiabilité et l\'amour du métier.' },
  { q: 'Le permis et le véhicule sont-ils vraiment obligatoires ?', a: 'Oui. Nos interventions ont lieu au domicile des clients dans un rayon de 40 km autour de Servins, souvent en zone rurale. Le permis B et un véhicule personnel sont donc indispensables.' },
  { q: 'Quel type de contrat proposez-vous ?', a: 'Un CDI dès l\'embauche, avec mutuelle d\'entreprise et primes. Le volume horaire est construit avec vous selon vos disponibilités et évolue avec votre portefeuille de clients attitrés.' },
  { q: 'Comment se déroule le processus de sélection ?', a: 'Candidature en ligne avec CV, échange téléphonique, entretien en agence, mise en situation accompagnée puis intégration. Nous sélectionnons avec soin car nos clients nous confient leur domicile.' },
  { q: 'Sous quel délai aurai-je une réponse ?', a: 'Nous accusons réception immédiatement par email et vous recontactons sous 5 jours ouvrés pour un premier échange.' },
];

const CV_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const CV_MAX = 5 * 1024 * 1024;

export default function DevenirEaserPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const cvRef = useRef(null);
  const [form, setForm] = useState({
    prenom: '', nom: '', email: '', telephone: '', ville: '', poste: '',
    permis: false, vehicule: false, experience: '', disponibilite: '', message: '',
    consent_rgpd: false, honeypot: ''
  });
  const [errors, setErrors] = useState({});

  const onCvChange = (file) => {
    if (!file) { setCvFile(null); return; }
    const okType = CV_TYPES.includes(file.type) || /\.(pdf|docx?)$/i.test(file.name);
    if (!okType) { toast.error('Format non accepté', { description: 'CV en PDF, DOC ou DOCX uniquement.' }); return; }
    if (file.size > CV_MAX) { toast.error('Fichier trop volumineux', { description: 'Votre CV doit faire moins de 5 Mo.' }); return; }
    setCvFile(file);
    setErrors(p => ({ ...p, cv: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.prenom.trim().length < 2) e.prenom = 'Prénom obligatoire';
    if (form.nom.trim().length < 2) e.nom = 'Nom obligatoire';
    if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email invalide';
    if (form.telephone.trim().length < 8) e.telephone = 'Téléphone obligatoire';
    if (!form.poste) e.poste = 'Choisissez un poste';
    if (!form.permis) e.permis = 'Le permis B est obligatoire';
    if (!form.vehicule) e.vehicule = 'Un véhicule personnel est obligatoire';
    if (!cvFile) e.cv = 'Le CV est obligatoire (PDF, DOC ou DOCX, 5 Mo max)';
    if (!form.consent_rgpd) e.consent_rgpd = 'Vous devez accepter la politique de confidentialité';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error('Vérifiez le formulaire', { description: 'Certains champs obligatoires sont manquants.' }); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === 'boolean' ? String(v) : v));
      fd.append('cv', cvFile);
      const res = await axios.post(`${BACKEND_URL}/api/candidatures`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        setSuccess(true);
        toast.success('Candidature envoyée !', { description: res.data.message });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error('Envoi impossible', { description: typeof detail === 'string' ? detail : 'Une erreur est survenue. Réessayez ou appelez-nous.' });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="py-20" data-testid="candidature-success">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="w-16 h-16 rounded-full bg-[hsl(var(--brand-mint))] flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-[hsl(var(--primary))]" />
          </div>
          <h1 className="font-['Space_Grotesk'] text-2xl font-semibold mb-3">Merci {form.prenom} ! Votre candidature est bien reçue.</h1>
          <p className="text-muted-foreground mb-2">Un email de confirmation vient de vous être envoyé. Notre équipe étudie votre CV et vous rappelle sous 5 jours ouvrés pour un premier échange.</p>
          <p className="text-sm text-muted-foreground mb-6">En attendant, découvrez notre vision du métier : « facilitateur de vies ».</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/notre-vision"><Button variant="outline">Notre vision</Button></Link>
            <Link to="/"><Button>Retour à l'accueil</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="devenir-easer-page">
      <SEOHead
        title="Devenir Easer — Recrutement CDI Artois"
        description="Rejoignez Ptitservices : postes d'Easer en CDI (ménage, repassage, jardinage, bricolage) autour de Servins, Lens, Arras, Béthune. Mutuelle, primes, clients attitrés. Permis B et véhicule obligatoires. Candidature en ligne avec CV."
        path="/devenir-easer"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "JobPosting",
            "title": "Easer — Aide à domicile (ménage, repassage, jardinage, bricolage)",
            "description": "Ptitservices recrute des Easers en CDI dans l'Artois : ménage, repassage, jardinage, petit bricolage au domicile de particuliers. Mutuelle, primes, clients attitrés, formation et suivi. Permis B et véhicule personnel obligatoires. Bienveillance et amour du métier avant tout.",
            "datePosted": new Date().toISOString().slice(0, 10),
            "employmentType": "FULL_TIME",
            "hiringOrganization": { "@type": "Organization", "name": "Ptitservices.com", "sameAs": "https://ptitservices.com", "logo": "https://customer-assets.emergentagent.com/job_easers-pro/artifacts/j38s08lp_LOGOSITE.png" },
            "jobLocation": { "@type": "Place", "address": { "@type": "PostalAddress", "streetAddress": "61 rue de la mairie", "addressLocality": "Servins", "postalCode": "62530", "addressRegion": "Hauts-de-France", "addressCountry": "FR" } },
            "applicantLocationRequirements": { "@type": "Country", "name": "France" },
            "directApply": true,
            "jobBenefits": "CDI, mutuelle d'entreprise, primes, clients attitrés, formation",
            "qualifications": "Permis B et véhicule personnel obligatoires. Bienveillance, fiabilité, amour du métier.",
            "identifier": { "@type": "PropertyValue", "name": "Ptitservices", "value": SAP_NUMBER }
          },
          faqSchema(FAQ)
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <motion.div className="lg:col-span-7" initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeUp}>
                <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">
                  <HandHeart className="h-4 w-4 mr-2" /> Recrutement — Nous embauchons en CDI
                </Badge>
              </motion.div>
              <motion.h1 variants={fadeUp} className="font-['Space_Grotesk'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-4" data-testid="devenir-easer-title">
                Devenir Easer : <span className="text-[hsl(var(--primary))]">un métier qui a du sens</span>, une équipe qui prend soin de vous
              </motion.h1>
              <motion.p variants={fadeUp} className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6 max-w-xl">
                Chez Ptitservices, un Easer n'est pas « une femme de ménage » ou « un jardinier » : c'est un <strong>facilitateur de vies</strong>. Vous suivez vos propres clients, vous êtes reconnu(e) pour votre travail, et vous rejoignez une équipe où la <strong>bienveillance</strong> et l'<strong>amour du métier</strong> passent avant tout.
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-2 mb-8">
                {['CDI', 'Mutuelle', 'Primes', 'Clients attitrés', 'Artois — 40 km autour de Servins'].map(t => (
                  <span key={t} className="px-3 py-1.5 rounded-full bg-card border border-border text-sm font-medium shadow-sm">{t}</span>
                ))}
              </motion.div>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <a href="#candidature">
                  <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white shadow-lg" data-testid="hero-apply-button">
                    Postuler maintenant <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <a href={`tel:${COMPANY_INFO.phoneRaw}`}>
                  <Button size="lg" variant="outline"><Phone className="mr-2 h-4 w-4" /> {COMPANY_INFO.phone}</Button>
                </a>
              </motion.div>
            </motion.div>
            <motion.div className="lg:col-span-5" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=700&h=520&fit=crop" alt="Easer souriante lors d'une intervention à domicile" className="w-full h-[300px] sm:h-[400px] object-cover" loading="eager" />
                <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur rounded-xl p-3 shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center"><Heart className="h-4 w-4 text-white" /></div>
                    <div>
                      <span className="text-sm font-semibold block">Bienveillance & amour du métier</span>
                      <span className="text-xs text-muted-foreground">Nos deux critères n°1 pour rejoindre l'équipe</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Ce que nous vous offrons</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Un vrai cadre pour exercer un vrai métier</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Parce qu'on ne peut pas prendre soin des autres sans être soi-même bien traité.</p>
          </motion.div>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger} data-testid="avantages-grid">
            {AVANTAGES.map((a) => (
              <motion.div key={a.title} variants={fadeUp}>
                <Card className="p-6 h-full border border-border shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center mb-3"><a.icon className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
                  <h3 className="font-['Space_Grotesk'] font-semibold mb-1">{a.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{a.desc}</p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Prérequis */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Le profil que nous cherchons</Badge>
              <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Nos clients nous confient leur domicile. Nous choisissons nos Easers avec soin.</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Un Easer entre dans l'intimité des foyers : celle d'une famille pressée, d'une personne âgée qui souhaite rester chez elle, d'un jardin qu'on aime. C'est pourquoi notre sélection est exigeante, mais toujours humaine.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Nous ne cherchons pas des « exécutants ». Nous cherchons des personnes qui aiment sincèrement rendre service, qui prennent soin des lieux comme s'il s'agissait des leurs, et qui ont envie de s'inscrire dans la durée auprès de leurs clients.
              </p>
            </motion.div>
            <motion.div className="space-y-4" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger} data-testid="prerequis-list">
              {PREREQUIS.map((p) => (
                <motion.div key={p.title} variants={fadeUp}>
                  <Card className={`p-5 flex items-start gap-4 ${p.required ? 'border-2 border-[hsl(var(--primary))]/20' : ''}`}>
                    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center flex-shrink-0"><p.icon className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-sm">{p.title}</h3>
                        <Badge className={p.required ? 'bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))] text-[10px]' : 'bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage))] text-[10px]'}>{p.required ? 'Obligatoire' : 'Un plus'}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{p.desc}</p>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Processus de sélection */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Notre processus de sélection</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">5 étapes, transparentes et bienveillantes</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Le même sérieux que nous mettons chez nos clients, nous le mettons dans le recrutement : c'est ce qui fait la qualité Ptitservices.</p>
          </motion.div>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger} data-testid="etapes-grid">
            {ETAPES.map((s, i) => (
              <motion.div key={s.num} variants={fadeUp} className="relative">
                <Card className="p-5 h-full border border-border shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-['Space_Grotesk'] text-3xl font-bold text-[hsl(var(--primary))]/20">{s.num}</span>
                    <div className="w-9 h-9 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center"><s.icon className="h-4.5 w-4.5 text-[hsl(var(--primary))]" /></div>
                  </div>
                  <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-2">{s.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
                </Card>
                {i < ETAPES.length - 1 && <ArrowRight className="hidden lg:block absolute top-1/2 -right-4 h-4 w-4 text-[hsl(var(--primary))]/40 -translate-y-1/2" />}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Formulaire */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60" id="candidature">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp} className="mb-6">
                <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Candidature en ligne</Badge>
                <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Postulez en 2 minutes</h2>
                <p className="text-muted-foreground">Joignez votre CV (PDF ou Word, 5 Mo max). Nous vous répondons sous 5 jours ouvrés.</p>
              </motion.div>
              <motion.form onSubmit={handleSubmit} initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp} data-testid="candidature-form" noValidate>
                <Card className="p-6 sm:p-8 space-y-5">
                  <input type="text" name="hp" value={form.honeypot} onChange={e => setForm(p => ({...p, honeypot: e.target.value}))} className="hidden" tabIndex={-1} autoComplete="off" />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="prenom">Prénom *</Label>
                      <Input id="prenom" value={form.prenom} onChange={e => setForm(p => ({...p, prenom: e.target.value}))} placeholder="Votre prénom" className="mt-1" data-testid="cand-prenom" />
                      {errors.prenom && <p className="text-xs text-destructive mt-1">{errors.prenom}</p>}
                    </div>
                    <div>
                      <Label htmlFor="nom">Nom *</Label>
                      <Input id="nom" value={form.nom} onChange={e => setForm(p => ({...p, nom: e.target.value}))} placeholder="Votre nom" className="mt-1" data-testid="cand-nom" />
                      {errors.nom && <p className="text-xs text-destructive mt-1">{errors.nom}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="votre@email.com" className="mt-1" data-testid="cand-email" />
                      {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <Label htmlFor="telephone">Téléphone *</Label>
                      <Input id="telephone" value={form.telephone} onChange={e => setForm(p => ({...p, telephone: e.target.value}))} placeholder="06 00 00 00 00" className="mt-1" data-testid="cand-telephone" />
                      {errors.telephone && <p className="text-xs text-destructive mt-1">{errors.telephone}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ville">Ville de résidence</Label>
                      <Input id="ville" value={form.ville} onChange={e => setForm(p => ({...p, ville: e.target.value}))} placeholder="Ex : Liévin" className="mt-1" data-testid="cand-ville" />
                    </div>
                    <div>
                      <Label>Poste souhaité *</Label>
                      <Select value={form.poste} onValueChange={v => setForm(p => ({...p, poste: v}))}>
                        <SelectTrigger className="mt-1" data-testid="cand-poste"><SelectValue placeholder="Choisissez un poste" /></SelectTrigger>
                        <SelectContent>{POSTES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                      </Select>
                      {errors.poste && <p className="text-xs text-destructive mt-1">{errors.poste}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Expérience</Label>
                      <Select value={form.experience} onValueChange={v => setForm(p => ({...p, experience: v}))}>
                        <SelectTrigger className="mt-1" data-testid="cand-experience"><SelectValue placeholder="Votre expérience" /></SelectTrigger>
                        <SelectContent>{EXPERIENCES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="dispo">Disponibilité</Label>
                      <Input id="dispo" value={form.disponibilite} onChange={e => setForm(p => ({...p, disponibilite: e.target.value}))} placeholder="Ex : dès que possible, temps plein" className="mt-1" data-testid="cand-disponibilite" />
                    </div>
                  </div>

                  {/* Prérequis obligatoires */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--brand-mint))]/30 border border-[hsl(var(--primary))]/20 space-y-3">
                    <p className="text-sm font-medium flex items-center gap-2"><Car className="h-4 w-4 text-[hsl(var(--primary))]" /> Conditions obligatoires pour le poste</p>
                    <label className="flex items-start gap-2 cursor-pointer text-sm">
                      <Checkbox checked={form.permis} onCheckedChange={v => setForm(p => ({...p, permis: !!v}))} className="mt-0.5" data-testid="cand-permis" />
                      <span>Je suis titulaire du <strong>permis B</strong> *</span>
                    </label>
                    {errors.permis && <p className="text-xs text-destructive -mt-1 ml-6">{errors.permis}</p>}
                    <label className="flex items-start gap-2 cursor-pointer text-sm">
                      <Checkbox checked={form.vehicule} onCheckedChange={v => setForm(p => ({...p, vehicule: !!v}))} className="mt-0.5" data-testid="cand-vehicule" />
                      <span>Je dispose d'un <strong>véhicule personnel</strong> pour me rendre chez les clients *</span>
                    </label>
                    {errors.vehicule && <p className="text-xs text-destructive -mt-1 ml-6">{errors.vehicule}</p>}
                  </div>

                  {/* CV */}
                  <div>
                    <Label>Votre CV * <span className="text-muted-foreground font-normal">(PDF, DOC ou DOCX — 5 Mo max)</span></Label>
                    <input ref={cvRef} type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={e => onCvChange(e.target.files?.[0])} data-testid="cand-cv-input" />
                    {!cvFile ? (
                      <button
                        type="button"
                        onClick={() => cvRef.current?.click()}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => { e.preventDefault(); onCvChange(e.dataTransfer.files?.[0]); }}
                        className={`mt-1 w-full flex flex-col items-center justify-center gap-2 p-6 rounded-lg border-2 border-dashed text-sm transition-colors hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--brand-mint))]/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] ${errors.cv ? 'border-destructive' : 'border-border'}`}
                        data-testid="cand-cv-dropzone"
                      >
                        <Upload className="h-6 w-6 text-[hsl(var(--primary))]" />
                        <span className="font-medium">Cliquez ou glissez votre CV ici</span>
                        <span className="text-xs text-muted-foreground">PDF, DOC, DOCX jusqu'à 5 Mo</span>
                      </button>
                    ) : (
                      <div className="mt-1 flex items-center gap-3 p-3 rounded-lg border border-[hsl(var(--primary))]/30 bg-[hsl(var(--brand-mint))]/30" data-testid="cand-cv-selected">
                        <FileText className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{cvFile.name}</p>
                          <p className="text-xs text-muted-foreground">{(cvFile.size / 1024).toFixed(0)} Ko</p>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => { setCvFile(null); if (cvRef.current) cvRef.current.value = ''; }} aria-label="Retirer le CV" data-testid="cand-cv-remove"><X className="h-4 w-4" /></Button>
                      </div>
                    )}
                    {errors.cv && <p className="text-xs text-destructive mt-1">{errors.cv}</p>}
                  </div>

                  <div>
                    <Label htmlFor="message">Quelques mots sur vous (optionnel)</Label>
                    <Textarea id="message" value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} placeholder="Pourquoi ce métier vous tient à cœur, ce que vous aimez faire..." className="mt-1" rows={4} data-testid="cand-message" />
                  </div>

                  <div>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <Checkbox checked={form.consent_rgpd} onCheckedChange={v => setForm(p => ({...p, consent_rgpd: !!v}))} className="mt-0.5" data-testid="cand-consent" />
                      <span className="text-xs text-muted-foreground">J'accepte que mes données et mon CV soient utilisés dans le cadre de ce recrutement (conservation 2 ans maximum, <Link to="/politique-confidentialite" className="underline">politique de confidentialité</Link>). *</span>
                    </label>
                    {errors.consent_rgpd && <p className="text-xs text-destructive mt-1">{errors.consent_rgpd}</p>}
                  </div>

                  <Button type="submit" disabled={loading} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" data-testid="candidature-submit-button">
                    {loading ? 'Envoi en cours...' : 'Envoyer ma candidature'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Card>
              </motion.form>
            </div>

            <div className="space-y-4">
              <Card className="p-5 bg-[hsl(var(--brand-mint))]/20 border-0">
                <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-2 flex items-center gap-2"><Heart className="h-4 w-4 text-[hsl(var(--primary))]" /> Ce qui compte pour nous</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">La bienveillance et l'amour du métier avant le CV parfait. Si vous aimez prendre soin des gens et des lieux, parlons-nous.</p>
              </Card>
              <Card className="p-5 bg-[hsl(var(--brand-mint))]/20 border-0">
                <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-2 flex items-center gap-2"><Car className="h-4 w-4 text-[hsl(var(--primary))]" /> Permis + véhicule</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">Indispensables : vous intervenez chez nos clients dans un rayon de 40 km autour de Servins.</p>
              </Card>
              <Card className="p-5 bg-[hsl(var(--brand-mint))]/20 border-0">
                <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-2 flex items-center gap-2"><Clock className="h-4 w-4 text-[hsl(var(--primary))]" /> Réponse sous 5 jours</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">Accusé de réception immédiat par email, puis un appel de notre équipe pour un premier échange.</p>
              </Card>
              <Card className="p-5">
                <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-3">Une question ?</h3>
                <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="flex items-center gap-2 text-sm text-[hsl(var(--primary))] font-medium"><Phone className="h-4 w-4" />{COMPANY_INFO.phone}</a>
                <p className="text-xs text-muted-foreground mt-2">Agence Artois — {COMPANY_INFO.fullAddress}</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-10" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">FAQ recrutement</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight">Vos questions avant de postuler</h2>
          </motion.div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="recrutement-faq">
            {FAQ.map((item, i) => (
              <AccordionItem key={item.q} value={`faq-${i}`} className="border border-border rounded-lg px-4 bg-card">
                <AccordionTrigger className="text-sm font-medium text-left hover:no-underline">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
